import { useState } from 'react'
import { Search, ChevronDown } from 'lucide-react'

const statusOptions = [
  'Ordered',
  'Sample Collected',
  'Sent to Lab',
  'Results Received',
  'Reviewed',
] as const

interface LabOrderSearchProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  statusFilter: string
  onStatusFilterChange: (status: string) => void
}

export function LabOrderSearch({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
}: LabOrderSearchProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)

  return (
    <div data-testid="lab-order-search" className="flex items-center gap-3 flex-wrap">
      {/* Search bar */}
      <div className="relative flex-1 min-w-[200px] max-w-[360px]">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          data-testid="lab-order-search-input"
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by patient name, order ID, or test name..."
          className="w-full h-7 pl-7 pr-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 placeholder:text-text-disabled"
        />
      </div>

      {/* Status filter dropdown */}
      <div className="relative">
        <button
          data-testid="lab-order-status-filter"
          onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Status:</span>
          {statusFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {statusDropdownOpen && (
          <div
            data-testid="lab-order-status-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[180px] max-h-40 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="status-option-all"
              onClick={() => {
                onStatusFilterChange('')
                setStatusDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {statusOptions.map((status) => (
              <button
                key={status}
                type="button"
                data-testid={`status-option-${status}`}
                onClick={() => {
                  onStatusFilterChange(status)
                  setStatusDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {status}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
